/**
 * @author NikolaV
 */

// pure
import { Events, EventsGlobal } from '@shared/Events';
import { MapClickMarkerLayer } from '@openlayers/MapClickMarkerLayer';
import { MapTileProgressBar } from '@openlayers/MapTileProgressBar';
import { IAllMapLayers } from '@openlayers/IAllMapLayers';
import { fitExtent, fitFeatures, fitProjectBounds as olFitProjectBounds, latLonToEN, panTo } from '@openlayers/ol-utils';
import { MapOverlays } from '@openlayers/ol-overlays';
import { BackgroundMaps } from '@openlayers/BackgroundMaps';
import { LayerWrapper } from '@openlayers/LayerWrapper';
import { IMapTilelayer } from '@openlayers/tile-layers/TileLayerService';
import { MapTileLayers } from '@openlayers/tile-layers/MapTileLayers';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';

// openlayers imports
import OlMap from 'ol/Map';
import OlView from 'ol/View';
import { transform as olTransform } from 'ol/proj';
import { ScaleLine as OlScaleLine, ZoomSlider as OlZoomSlider } from 'ol/control';
import { defaults as defaultInteractions } from 'ol/interaction.js';
import OlMouseWheelZoom from 'ol/interaction/MouseWheelZoom';
import * as olExtent from 'ol/extent';
import Feature from 'ol/Feature';
import TileLayer from 'ol/layer/Base';
import VectorSource from 'ol/source/Vector';
import { Coordinate } from 'ol/coordinate';
import VectorLayer from 'ol/layer/Vector';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { Notify } from '@shared/ui-native/Notify';

//Analytics
import { wirePendoToOpenLayers, pendoLayerToggled } from '../../analytics/pendo-ol';


export class MapWrapper implements IStateful<IMapWrapperState> {
    private instance: OlMap = null;
    private domEl: HTMLElement = null;

    // view
    minZoom = 3;
    maxZoom = 27;
    animationDuration = 400; // map pan and zoom animation

    // base/tile layers
    tileLayers: MapTileLayers = null;

    // feature layers
    featureLayers: IAllMapLayers = null;
    clickMarkerLayer = new MapClickMarkerLayer();

    // progress indicator
    progressBar: MapTileProgressBar = new MapTileProgressBar();

    // background maps
    backgroundMaps: BackgroundMaps = new BackgroundMaps(this.progressBar);

    constructor() {
        // Use correct proj4 if one exists that can be shared with OpenLayers
        if (kuura.getProjectProj4Code() !== 'EPSG:4326') {
            proj4.defs(kuura.getProjectProj4Code(), kuura.getProjectProj4Text());
            register(proj4);
        }
    }

    init(domTargetId, center, zoom, tileLayerDefinitions: IMapTilelayer[]) {
        this.domEl = document.querySelector('#' + domTargetId);

        this.instance = new OlMap({
            target: domTargetId,

            view: new OlView({
                center,
                zoom,
                minZoom: this.minZoom,
                maxZoom: this.maxZoom,
                constrainResolution: false
            }),

            layers: [],

            controls: [
                new OlZoomSlider(),
                new OlScaleLine({
                    minWidth: 100,
                }),
            ],

            moveTolerance: 2,

            // disable smooth zoom
            interactions: defaultInteractions({
                mouseWheelZoom: false,
                doubleClickZoom: false, // double click closes polygon drawing with tools, so its weird when it also zoomes in
            }).extend([
                new OlMouseWheelZoom({ duration: 0 }),
            ]),
        });

        wirePendoToOpenLayers(this.instance); //Analytics

        this.tileLayers = new MapTileLayers(this.instance, tileLayerDefinitions);

        this.addLayer(this.backgroundMaps.layer, 15, 'bg-maps');
        this.addLayer(this.clickMarkerLayer.layer, 100, 'click-marker');

        this.handleWindowsResize();
        this.tileLayers.activateByIdOrUuid('-1');

        const triggerOnViewChange = _.throttle(() => Events.trigger('map-view-changed'), 200);
        this.instance.getView().on('change:resolution', triggerOnViewChange);

        Events.on('map-hide-overlays', () => {
            MapOverlays.hideDialog(this.instance);
            MapOverlays.hideTooltip(this.instance);
            MapOverlays.hideContextMenu(this.instance);
        });

        Events.on(EventsGlobal.MAP_FIT_EXTENT, (e, { extent }) => this.fitExtent(extent));

        Events.on('map-fit-model', (e, { id }) => this.fitFeatureById(id));

        Events.on(EventsGlobal.MAP_FIT_DOCUMENTS, this.fitDocumentsLayer);

        Events.on(EventsGlobal.MAP_FIT_PHOTOS, this.fitImagesLayer);

        return this;
    }

    // expose only some of native map functionality, other should be done through this wrapper
    getInstance = (): OlMap => this.instance;

    addLayer = (layer: TileLayer, zIndex: number, id: string) => {
        try {
            this.instance.addLayer(layer);
            layer.setZIndex(zIndex);
            layer.set('id', id);
            //Send pendo events when layer visibility changes
            layer.on('change:visible', () => {
            pendoLayerToggled(layer, layer.getVisible());
            });

        } catch (e) {
            console.warn('[ map wrapper ] Couldnt add layer: ', id, e);
        }
    };

    removeLayer = (layer: TileLayer) => {
        try {
            this.instance.removeLayer(layer);
        }
        catch (e) {
            console.warn('[ map wrapper ] Couldnt remove layer', e);
        }
    };

    getLayerById = (layerId: string) => {
        let found = null;
        this.instance.getLayers().forEach(layer => {
            if (layer && layer.get('id') === layerId) {
                found = layer;
            }
        });
        return found;
    }

    removeLayerById = (layerId: string) => {
        this.instance.getLayers().forEach(layer => {
            if (layer && layer.get('id') === layerId) {
                this.removeLayer(layer);
            }
        });
    }

    setLayersOpacityByIds = (layerIds: string[], opacity: number) => {
        this.instance.getLayers().forEach(layer => {
            if (layer && layerIds.includes(layer.get('id'))) {
                layer.setOpacity(opacity);
            }
        });
    }

    setFeatureLayers = (layers: IAllMapLayers) => {
        this.featureLayers = layers;

        // disable rasterization and stringlines when rasterized model is removed (unchecked)
        this.featureLayers.models.onFeaturesCollectionChange = (layer, newFeatures) => {
            this.backgroundMaps.getRasterizedModelIds().forEach(id => {
                const rasterFound = this.featureLayers.models.getFeaturesBySource({ modelId: id });
                if (!rasterFound || rasterFound.length == 0) {
                    this.backgroundMaps.getRasterizedModelIds().delete(id);
                    this.backgroundMaps.load();
                }
            });

            Object.keys(this.featureLayers.stringlines.overlayModelFeaturesById).forEach(id => {
                const stringlineFound = this.featureLayers.models.getFeaturesBySource({ modelId: parseInt(id) });
                if (!stringlineFound || stringlineFound.length == 0) {
                    this.featureLayers.stringlines.removeOverlayModel(parseInt(id));
                }
            });
        };
    }

    getCenter = (): number[] => {
        return this.instance.getView().getCenter();
    };

    getCoordinateFromPixel = (pixel: number[]): number[] => {
        return this.instance.getCoordinateFromPixel(pixel);
    };

    getViewExtent = (): number[] => {
        return this.instance.getView().calculateExtent(this.instance.getSize());
    };

    setCenter = (centerNUnsafe: Coordinate): void => {
        if (centerNUnsafe && typeof centerNUnsafe[0] === 'number' && typeof centerNUnsafe[1] === 'number') {
            this.instance.getView().setCenter(centerNUnsafe);
        }
        else {
            this.fitProjectBounds();
        }
    };

    getZoom = (): number => {
        return this.instance.getView().getZoom() || 8;
    };

    setZoom = (zoomUnsafe: number): void => {
        const zoom = utils.clamp(0, zoomUnsafe || 10, this.maxZoom);
        this.instance.getView().setZoom(zoom);
    };

    setRotation = (rotation: number): void => {
        this.instance.getView().setRotation(rotation);
    };

    getProjection = () => this.instance.getView().getProjection();

    fitExtent = (extent: olExtent.Extent, animationDuration = 250) => fitExtent(this.instance, extent, animationDuration);

    fitVisible = (initialExtent?) => {
        const totalExtent = initialExtent ?? olExtent.createEmpty();

        // with excluded zero
        [this.featureLayers.documents,
        this.featureLayers.images]
            .filter(Boolean)
            .forEach(layer => olExtent.extend(totalExtent, layer.getExtentExcludeZero()));

        // full extents
        [this.featureLayers.alignments,
        this.featureLayers.logpoints,
        this.featureLayers.models,
        this.featureLayers.pipes,
        this.featureLayers.points,
        this.featureLayers.stringlines,
        this.featureLayers.vehicles]
            .filter(Boolean)
            .forEach(layer => olExtent.extend(totalExtent, layer.layerSource.getExtent()));

        fitExtent(this.instance, totalExtent);
    };

    fitAllLayers = () => {
        const extent: olExtent.Extent = olExtent.createEmpty();
        this.getInstance().getLayers().forEach(l => {
            try {
                olExtent.extend(extent, (l as VectorLayer<any>).getSource().getExtent());
            }
            catch (e) { }
        });
        this.fitExtent(extent);
    }

    fitFeatures = (features: Feature[], warn = true) => {
        fitFeatures(this.instance, features, warn);
    };

    fitLayerSource = (layerSource: VectorSource) => {
        fitExtent(this.instance, layerSource.getExtent());
    };

    fitLogpointsLayer = () => {
        fitFeatures(this.instance, this.featureLayers.logpoints.getVisibleFeatures());
    };

    fitImagesLayer = () => {
        fitFeatures(this.instance, this.featureLayers.images.layerSource.getFeatures());
    };

    fitDocumentsLayer = () => {
        fitFeatures(this.instance, this.featureLayers.documents.layerSource.getFeatures());
    };

    fitVehiclesLayer = () => {
        fitFeatures(this.instance, this.featureLayers.vehicles.layerSource.getFeatures());
    };

    fitFeatureById = (modelId: number) => {
        const featureLayers: LayerWrapper<any>[] = Object.values(this.featureLayers).filter(Boolean);

        for (const layer of featureLayers) {
            try {
                const feature = layer.getFeatureById(modelId) || layer.getFeaturesBySource({ modelId })[0];
                if (feature) {
                    this.fitFeatures([feature]);
                    break;
                }
            } catch { }
        }
    }

    async fitProjectBounds(): Promise<void> {
        return await olFitProjectBounds(this.instance);
    }

    panTo = (e, n, zoom?) => panTo(this.instance, e, n, zoom);

    setMapCursor(cursor: '' | 'crosshair' | 'pointer' | 'not-allowed') {
        (this.getInstance().getTargetElement() as HTMLElement).style.cursor = cursor;
    }

    events = {
        onClick: (callback: (evt: ClickEventWithCoordinate) => any) => {
            let startX = 0;
            let startY = 0;
            this.instance.getViewport().addEventListener('pointerdown', (evt: HTMLElementEventMap['pointerdown']) => {
                startX = evt.clientX;
                startY = evt.clientY;
            })
            this.instance.getViewport().addEventListener('click', (evt: ClickEventWithCoordinate) => {
                if ((evt.target as HTMLElement).tagName.toLowerCase() !== 'canvas') return;

                if (Math.abs(startX - evt.clientX) < 3 && Math.abs(startY - evt.clientY) < 3) {
                    evt.coordinate = this.instance.getEventCoordinate(evt);
                    callback(evt);
                }
            });
        },
        onClickOnce: (callback: (BaseEvent) => any) => this.instance.once('click', callback),
        onMouseMove: (callback: (BaseEvent) => any) => this.instance.on('pointermove', callback),
        onRightClick: (callback: (evt: ClickEventWithCoordinate) => any) => {
            this.instance.getViewport().addEventListener('contextmenu', (evt: ClickEventWithCoordinate) => {
                evt.coordinate = this.instance.getEventCoordinate(evt);
                callback(evt);
            })
        },
        onZoom: (callback: (BaseEvent) => any) => this.instance.getView().on('change:resolution', callback),
        onRotation: (callback: (BaseEvent) => any) => this.instance.getView().on('change:rotation', callback),
        triggerOnZoom: () => this.instance.getView().dispatchEvent('change:resolution'),
        onMove: (callback: (BaseEvent) => any) => this.instance.getView().on('change:center', callback)
    };

    stateful = {
        defaultState: {
            clickMarker: null
        },
        getCurrentState: () => {
            const clickMarker = this.clickMarkerLayer.clickMarkerPosition;
            const center = this.getCenter();
            const [lon, lat] = olTransform(center, 'EPSG:3857', 'EPSG:4326');

            return {
                clickMarker: {
                    lat: clickMarker ? olTransform(clickMarker, 'EPSG:3857', 'EPSG:4326')[1] : null,
                    lon: clickMarker ? olTransform(clickMarker, 'EPSG:3857', 'EPSG:4326')[0] : null,
                },
                view: {
                    zoom: this.getZoom() || 8,
                    lat: isNaN(lat) ? 0 : lat,
                    lon: isNaN(lon) ? 0 : lon,
                    rotation: this.getInstance().getView().getRotation()
                },
                baseLayers: this.tileLayers.stateful.getCurrentState(),
                showModelIdsLines: this.featureLayers.stringlines.getOverlayModelIds()
            };
        },
        restoreDefaultState: () => {
            this.tileLayers.stateful.restoreDefaultState();
            this.stateful.restoreState(this.stateful.defaultState);
        },
        restoreState: (state: IMapWrapperState) => {
            console.log('[ map wrapper ] Restoring state', state);

            this.tileLayers.stateful.restoreState(state.baseLayers);

            if (state.view) {
                this.setZoom(state.view.zoom);
                this.setCenter(latLonToEN(state.view.lat, state.view.lon));

                if (state.view.hasOwnProperty('rotation')) {
                    this.getInstance().getView().setRotation(state.view.rotation);
                }

                if (state.clickMarker) {
                    const clickNE: Coordinate = olTransform([state.clickMarker.lon, state.clickMarker.lat], 'EPSG:4326', 'EPSG:3857');
                    this.clickMarkerLayer.drawClickMarker(...clickNE as [number, number]);
                }

                if (state.showModelIdsLines?.length > 0) {
                    state.showModelIdsLines.forEach(id => {
                        this.featureLayers.stringlines.toggleOverlayModel(id);
                    })
                }

                if (PageStateUtils.stateHasKey<IMapWrapperState>(state, 'tileLayerId')) {
                    this.tileLayers.activateByIdOrUuid(state.tileLayerId);
                }

                // legacy
                if (PageStateUtils.stateHasKey<IMapWrapperState>(state, 'tileLayerOpacity')) {
                    const layer = this.tileLayers.getLayerByUuidOrId(state.tileLayerId);
                    if (layer) {
                        this.tileLayers.setOpacity(state.tileLayerId as string, state.tileLayerOpacity);
                    }
                }

                console.log(this.tileLayers)
            }
        },
    };

    private handleWindowsResize = () => {
        $(window).resize(_.throttle(() => {
            const headerHeight = $('#app__header').outerHeight();
            const height = $(window).height() - (headerHeight || 0) + 'px';
            const width = $(window).width() + 'px';

            $(this.domEl).css({
                height,
                width,
            });

            if (this.instance) {
                this.instance.updateSize();
            }
        }, 300));

        setTimeout(() => {
            $(window).trigger('resize');
        }, 100);
    }
}
